<template>
  <div>
    <img id="logoid" class="hidden" src="./img/logo_ripph.png">
    <img id="VSid" class="hidden" src="./img/vs.png">
    <img id="Sid" class="hidden" src="./img/s.png">
    <img id="Uid" class="hidden" src="./img/u.png">
    <img id="VUid" class="hidden" src="./img/vu.png">

    <ReportDropdownList v-show="recipient"
                        ref="reportDropdown"
                        :recipient="recipient"
                        :create-tool-tip="createToolTip"
                        :evaluations="evaluations"
                        :current-tab="currentTab"
                        :snapshots="snapshots"
                        :snapshot-kid="snapshotKid"
                        :snapshot-adult="snapshotAdult"
                        :snapshot-young="snapshotYoung"
                        :entries="entries"
                        @on-visualize="onVisualize"
                        @on-pdf-export="downloadPDF"
                        @on-word-export="downloadWord"
                        @on-csv-export="downloadCsv"
    />
    <hr>
    <div v-if="!checkCombo()">
      <h4>{{ $t('evaluation.charts.chose_data') }}</h4>
    </div>
    <ReportSnapshotDetails v-if="checkCombo()"
                           :recipient="recipient"
                           :snapshots="selectedSnapshots"
                           :graph-type="graphType"
                           :report-type="reportType"
                           :section="entryType.name"/>
    <div v-show="displayAll">
      <div class="row justify-content-center " style="min-width:500px; overflow: auto">
        <div class="col col-md-8 col-lg-5">
          <RadarChart v-show="(reportType === 'scores' && graphType === 'radar') || (reportType === 'summary' && graphType === null)"
                      ref="graphRadarChart"
                      :chart-data="charts.radarChart.data"
                      :options="charts.radarChart.options"
                      :height="400"
          />
        </div>
      </div>
      <ReportTable v-if="graphType === 'table'"
                   ref="reportTable"
                   :type="reportType"
                   :evaluation="getRightEvaluationByRecipientAge()[0]"
                   :snapshots="selectedSnapshots"
                   :entries="setLabelsForTable()"
      />
      <!-- ONLY 1 SNAPSHOT SELECTED -->
      <div v-show="selectedSnapshots.length === 1">
        <HistogramPerSection v-show="(reportType === 'scores' && graphType === 'avg_histogram') || (reportType === 'summary' && graphType === null)"
                             ref="graphHistogramPerSection"
                             :chart-data="charts.chartHistogramPerSection.data"
                             :options="charts.chartHistogramPerSection.options"
                             :height="100" />

        <div v-show="(reportType === 'satisfaction' && graphType === 'pies') || (reportType === 'summary' && graphType === null)"
             class="row">
          <div v-for="(dataset, index) in satisfactionGraphDatasets" ref="allSatifactionCharts" :key="index" class="col-3">
            <SatisfactionPieChart :ref="'graphSatisfactionPieChart-'+index"
                                  :chart-id="'satisfaction-chart-' + index"
                                  :chart-data="__prepareDataForSatisfactionGraph(index, dataset)"
                                  :options="__prepareOptionsForSatisfactionGraph(index)"
                                  :height="150"/>
          </div>
        </div>
      </div>
      <!-- MULTIPLE SNAPSHOTS SELECTED -->
      <div v-show="selectedSnapshots.length > 1">
        <MultipleHistograms v-show="(reportType === 'scores' && graphType === 'avg_histogram') || (reportType === 'summary' && graphType === null)"
                            ref="graphMultipleHistograms"
                            :chart-data="charts.chartMultipleHistograms.data"
                            :chart-id="'multiple-scores-chart'"
                            :options="charts.chartMultipleHistograms.options"
                            :height="100" />
        <MultipleSatisfaction v-show="(reportType === 'satisfaction' && graphType === 'stacked_bars') || (reportType === 'summary' && graphType === null)"
                              ref="graphMultipleSatisfaction"
                              :chart-id="'multiple-satisfaction-chart'"
                              :chart-data="charts.chartMultipleSatisfaction.data"
                              :options="charts.chartMultipleSatisfaction.options"
                              :height="100" />
      </div>
     <label for="story">Commentaire:</label>
      <textarea  v-model="comment" cols="250" rows="2">

      </textarea>
      <!-- MODALS -->
      <b-modal v-if="selectedSnapshots.length >= 1" ref="warningPDF"
               :title="$t('evaluation.pdf.modal_title')"
               ok-variant="info"
               centered
               @cancel.prevent="closeWarningPDF()"
               @ok.prevent="okWarningPDF()">
        <p class="text-info">{{ $t('evaluation.pdf.modal_text') }}</p>
      </b-modal>

      <b-modal v-if="selectedSnapshots.length >= 1" ref="warningWord"
               :title="$t('evaluation.word.modal_title')"
               ok-variant="info"
               centered
               @cancel.prevent="closeWarningWord()"
               @ok.prevent="okWarningWord()">
        <p class="text-info">{{ $t('evaluation.word.modal_text') }}</p>
      </b-modal>

    </div>
  </div>
</template>

<script>
import Calculations from '~/utils/Calculations'
import HistogramPerSection from './reports/charts/HistogramPerSection'
import MultipleHistograms from './reports/charts/MultipleHistograms'
import HistogramOverall from './reports/charts/HistogramOverall'
import CategoryEndorsment from './reports/charts/CategoryEndorsment'
import CategoryEndorsmentOverall from './reports/charts/CategoryEndorsmentOverall'
import ReportDropdownList from './ReportDropdownList'
import RadarChart from './RadarChart'
import SatisfactionPieChart from './reports/charts/SatisfactionPieChart'
import MultipleSatisfaction from './reports/charts/MultipleSatisfaction'
import ReportSnapshotDetails from './reports/ReportSnapshotDetails'
import jsPDF from 'jspdf'
import wordTemplate from '~/data/WordTemplate.html'
import { mapGetters } from 'vuex'
import ChartJsPluginDataLabels from 'chartjs-plugin-datalabels'
import ReportTable from './reports/ReportTable'

export default {
  name: 'Reports',
  components: {
    SatisfactionPieChart,
    MultipleSatisfaction,
    RadarChart,
    HistogramPerSection,
    MultipleHistograms,
    HistogramOverall,
    CategoryEndorsment,
    CategoryEndorsmentOverall,
    ReportTable,
    ReportDropdownList,
    jsPDF,
    ReportSnapshotDetails,
    ChartJsPluginDataLabels
  },
  mixins: [Calculations],
  props: {
    recipient: { required: true, type: Object },
    evaluations: '',
    currentTab: 0,
    snapshots: { type: Array },
    snapshotKid: '',
    snapshotAdult: '',
    snapshotYoung: '',
    createToolTip: ''
  },
  data: () => ({
    /* LABELS */
    comment:"",
    tChart: 'evaluation.charts.',
    satisfactionLabels: [
      'evaluations.head.criteria.very_satisfied',
      'evaluations.head.criteria.satisfied',
      'evaluations.head.criteria.unsatisfied',
      'evaluations.head.criteria.very_unsatisfied'
    ],
    /*GRAPH*/
    ourLabels: ['total',
      'regular_activity',
      'communication', 'moving', 'nutrition',
      'physical_psy_condition', 'healing', 'dwelling',
      'social_roles',
      'responsibility', 'personal_relation', 'associative_life',
      'main_activity', 'hobby'],
    ourKidLabels: ['total',
      'regular_activity',
      'communication', 'moving', 'nutrition',
      'physical_psy_condition', 'healing', 'dwelling',
      'social_roles',
      'responsibility', 'personal_relation', 'associative_life',
      'main_activity', 'hobby'],
    ourLabelsRadar: [
      'communication', 'moving', 'nutrition',
      'physical_psy_condition', 'healing', 'dwelling',
      'responsibility', 'personal_relation', 'associative_life',
      'main_activity', 'hobby'],
    ourKidLabelsRadar: [
      'communication', 'moving', 'nutrition',
      'physical_psy_condition', 'healing', 'dwelling',
      'responsibility', 'personal_relation', 'associative_life',
      'main_activity','hobby'],
    /* COLORS */
    colors: ['rgba(31, 119, 180, ', 'rgba(255, 127, 14, ', 'rgba(44, 160, 44, ', 'rgba(214, 39, 40, ',
      'rgba(148, 103, 189, ', 'rgba(140, 86, 75, ', 'rgba(227, 119, 194, ', 'rgba(127, 127, 127, ',
      'rgba(188, 189, 34, ', 'rgba(23, 190, 207, '],
    satisfactionColors: ['rgba(216,228,188, 0.7)', 'rgba(255,255,204, 0.7)', 'rgba(255,181,108, 0.7)', 'rgba(237,82,70, 0.7)'],
    /* DATA FROM DROPDOWNLISTS */
    selectedSnapshots: [],
    currentSnapshot: null,
    reportType: null,
    graphType: null,
    entries: [],
    entryType: null,
    /* CHARTS */
    charts: {
      chartHistogramPerSection: {
        data: {
          labels: [], // titles sections
          datasets: [{
            label: '',
            data: [
              8,
              6,
              2, 7, 2,
              4, 6, 2,
              9,
              9, 10, 10,
              8, 3, 4
            ],
            borderWidth: 1
          }]
        },
        options: {
          tooltips: {
            callbacks: {
              label: function (tooltipItem, data) {
                return tooltipItem.yLabel.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
              }
            }
          },
          responsive: true,
          maintainAspectRatio: true,
          legend: {
            display: false
          },
          plugins: {
            datalabels: {
              formatter: function (value) {
                return value ? value.toFixed(2) : 0
              }
            }
          },
          scales: {
            yAxes: [
              {
                display: true,
                ticks: {
                  beginAtZero: true,
                  min: 0,
                  max: 10
                }
              }
            ]
          }
        }
      },
      chartMultipleHistograms: {
        data: {
          labels: ['Chocolate', 'Vanilla', 'Strawberry'],
          datasets: []
        },
        options: {
          tooltips: {
            callbacks: {
              label: function (tooltipItem, data) {
                return tooltipItem.yLabel.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
              }
            }
          },
          barValueSpacing: 20,
          scales: {
            yAxes: [{
              ticks: {
                min: 0
              }
            }]
          },
          plugins: {
            datalabels: {
              formatter: function (value) {
                return value ? value.toFixed(2) : 0
              }
            }
          }
        }
      },
      chartHistogramOverall: {
        data: {
          labels: [], // titles sections
          datasets: [{
            label: '',
            data: [8, 6, 2, 7, 2, 4, 6, 2, 9, 9, 10, 10, 8, 3, 4],
            backgroundColor: [
              'rgba(75, 192, 192, 0.2)',
              'rgba(255, 205, 86, 0.2)',
              'rgba(255, 159, 64, 0.2)', 'rgba(255, 159, 64, 0.2)', 'rgba(255, 159, 64, 0.2)',
              'rgba(255, 159, 64, 0.2)', 'rgba(255, 159, 64, 0.2)', 'rgba(255, 159, 64, 0.2)',
              'rgba(255, 205, 86, 0.2)',
              'rgba(255, 159, 64, 0.2)', 'rgba(255, 159, 64, 0.2)', 'rgba(255, 159, 64, 0.2)',
              'rgba(255, 159, 64, 0.2)', 'rgba(255, 159, 64, 0.2)', 'rgba(255, 159, 64, 0.2)'
            ],
            borderColor: [
              'rgba(75, 192, 192, 1)',
              'rgba(255, 205, 86, 1)',
              'rgba(255, 159, 64, 1)', 'rgba(255, 159, 64, 1)', 'rgba(255, 159, 64, 1)',
              'rgba(255, 159, 64, 1)', 'rgba(255, 159, 64, 1)', 'rgba(255, 159, 64, 1)',
              'rgba(255, 205, 86, 1)',
              'rgba(255, 159, 64, 1)', 'rgba(255, 159, 64, 1)', 'rgba(255, 159, 64, 1)',
              'rgba(255, 159, 64, 1)', 'rgba(255, 159, 64, 1)', 'rgba(255, 159, 64, 1)'
            ],
            borderWidth: 1
          }]
        },
        options: {
          tooltips: {
            callbacks: {
              label: function (tooltipItem, data) {
                return tooltipItem.yLabel.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
              }
            }
          },
          responsive: true,
          maintainAspectRatio: true,
          legend: {
            display: false
          },
          plugins: {
            datalabels: {
              formatter: function (value) {
                return value.toFixed(2)
              }
            }
          },
          scales: {
            yAxes: [
              {
                display: true,
                ticks: {
                  beginAtZero: true,
                  min: 0,
                  max: 10
                }
              }
            ]
          }
        }
      },
      chartCategoryEndorsment: {
        data: {
          labels: [], // titles sections
          datasets: [
            {
              label: '',
              stack: 1,
              data: [],
              fill: true,
              borderColor: 'rgb(220, 230,241)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              borderWidth: 1
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          legend: {
            labels: {
              filter: function (item, chart) {
                return item.text.length !== 0
              }
            }
          },
          plugins: {
            datalabels: {
              formatter: function (value) {
                return value.toFixed(2)
              }
            }
          },
          scales: {
            yAxes: [
              {
                stacked: true,
                display: true,
                ticks: {
                  beginAtZero: true,
                  min: 0,
                  max: 100
                }
              }
            ],
            xAxis: [
              {
                stacked: true
              }
            ]
          }
        }
      },
      chartMultipleSatisfaction: {
        data: {
          labels: [],
          datasets: [
            {
              label: 'My First dataset',
              backgroundColor: 'rgba(99,255,132,0.2)',
              data: [59, 80, 81, 56, 55, 40, 65, 59, 80, 81, 56, 55, 40, 65],
              stack: 1
            },
            {
              label: 'My Second dataset',
              backgroundColor: 'rgba(99,132,255,0.2)',
              data: [80, 81, 56, 55, 40, 0, 60, 59, 80, 81, 56, 55, 40, 65],
              stack: 2
            },
            {
              label: 'My Third dataset',
              backgroundColor: 'rgba(255,99,132,0.2)',
              data: [60, 59, 80, 81, 56, 55, 40, 59, 80, 81, 56, 55, 40, 65],
              stack: 2
            }
          ]
        },
        options: {
          legend: {
            display: false
          },
          plugins: {
            datalabels: {
              formatter: function (value) {
                if (value) {
                  return Math.round(value) + '%'
                } else {
                  return ''
                }
              }
            }
          },
          responsive: true,
          maintainAspectRatio: true,
          scales: {
            yAxes: [{
              ticks: {
                max: 100
              },
              stacked: true
            }]
          }
        }
      },
      chartCategoryEndorsmentOverall: {
        data: {
          labels: [], // titles sections
          datasets: [
            {
              label: '',
              stack: 1,
              data: [

              ],
              fill: true,
              borderColor: 'rgb(220, 230,241)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              borderWidth: 1
            },
            {
              label: '',
              stack: 1,
              data: [
                7,

                8,
                6, 7, 7,
                4, 6, 4,

                2,
                8, 7, 4,
                8, 6, 5
              ],
              fill: false,
              borderColor: 'rgb(220,230,241)',
              borderWidth: 1
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          legend: {
            labels: {
              filter: function (item, chart) {
                return item.text.length !== 0
              }
            }
          },
          plugins: {
            datalabels: {
              formatter: function (value) {
                return value.toFixed(2)
              }
            }
          },
          tooltips: {
            filter: function (tooltipItem) {
              return tooltipItem.datasetIndex === 0
            }
          },
          scales: {
            yAxes: [
              {
                stacked: true,
                display: true,
                ticks: {
                  beginAtZero: true,
                  min: 0,
                  max: 100
                }
              }
            ],
            xAxis: [
              {
                stacked: true
              }
            ]
          }
        }
      },
      radarChart: {
        data: {
          labels: []
        },
        options: {
          radar: {}, // empty object is mandatory
          scale: {
            ticks: {
              beginAtZero: true,
              min: 0,
              max: 10
            }
          }, // empty object is mandatory
          polycolor: 'rgba(250, 100, 50, .5)',
          responsive: true,
          maintainAspectRatio: true,
          plugins: {
            datalabels: {
              formatter: Math.round
            }
          },
          tooltips: {
            callbacks: {
              label: function (tooltipItem, data) {
                return tooltipItem.yLabel.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
              }
            }
          }
        }
      },
      satisfactionGraphs: {
        data: [],
        options: []
      },
      satisfactionGraphsOverall: {
        data: [],
        options: []
      }
    },
    satisfactionDataOptions: {
      labels: [],
      datasets: [{
        backgroundColor: [
          'rgba(216,228,188,1)',
          'rgba(255,255,204,1)',
          'rgba(255,181,108,1)',
          'rgba(237,82,70,1)'
        ],
        borderWidth: 0
      }]
    },
    satisfactionGraphDatasets: [],
    satisfactionDataOverallOptions: {
      labels: [],
      datasets: [{
        backgroundColor: [
          'rgba(216,228,188,1)',
          'rgba(255,255,204,1)',
          'rgba(255,181,108,1)',
          'rgba(237,82,70,1)'
        ],
        borderWidth: 0
      }]
    },
    satisfactionGraphOverallDatasets: [],
    /* REPORTS UTILS */
    docPDF: null,
    docTitle: 'evaluation.pdf.pdftitle',
    labelsPDF: {
      title: 'evaluation.pdf.title',
      associationTitle: 'evaluation.pdf.associationTitle',
      creationDate: 'evaluation.pdf.creationDate',
      prof: 'evaluation.pdf.prof',
      recipient: 'evaluation.pdf.recipient',
      snapshotName: 'evaluation.pdf.snapshotName',
      lastUpdate: 'evaluation.pdf.lastUpdate',
      comment: 'evaluation.pdf.comment',
      scoresChart: 'evaluation.pdf.scoresChart',
      endorsmentChart: 'evaluation.pdf.endorsmentChart',
      satisfactionChart: 'evaluation.pdf.satisfactionChart',
      radarChart: 'evaluation.pdf.radarChart'
    },
    logo: null,
    preHtml: "<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><title>MHAVIE</title></head><body>",
    postHtml: '</body></html>',
    wordLandscape: '@page WordSection1{size: 841.95pt 595.35pt;mso-page-orientation: landscape;}div.WordSection1 {page: WordSection1;}',
    displayAll: false
  }),
  computed: {
    ...mapGetters({
      user: 'auth/user',
      evaluationsTypes: 'evaluations/evaluations'
    })
  },
  created () {
    this.setLabels()
  },
  mounted () {
    // Load the logo
    var c = document.createElement('canvas')
    var img = document.getElementById('logoid')
    setTimeout(() => {
      c.height = img.naturalHeight
      c.width = img.naturalWidth
      var ctx = c.getContext('2d')
      ctx.drawImage(img, 0, 0, c.width, c.height)
      this.logo = c.toDataURL()
    }, 500)
  },
  methods: {
    getEvaluationSectionTitle (evaluation, id) {
      if (evaluation.title !== 'kid') {
        return this.tChart + this.ourLabelsRadar[id]
      } else {
        return this.tChart + this.ourKidLabelsRadar[id]
      }
    },
    getEvaluationSectionTitleSatisfaction (evaluation, id) {
      if (evaluation.title !== 'kid') {
        return this.tChart + this.ourLabels[id]
      } else {
        return this.tChart + this.ourKidLabels[id]
      }
    },
    getAge () {
      let userAge = new Date().getFullYear() - this.recipient.birth_year
      if (userAge > 4) {
        return true
      }
      return false
    },
    getEvaluation (evaluationId) {
      return this.evaluations.filter(evaluation => evaluation.id === evaluationId)
    },
    setLabels () {
      let userAdult = this.getAge()

      this.charts.chartHistogramPerSection.data.labels = []
      this.charts.chartHistogramOverall.data.labels = []
      this.charts.chartMultipleHistograms.data.labels = []
      this.charts.chartMultipleSatisfaction.data.labels = []
      this.charts.chartCategoryEndorsment.data.labels = []
      this.charts.chartCategoryEndorsmentOverall.data.labels = []
      this.charts.radarChart.data.labels = []
      this.entries = []

      if (userAdult) {
        for (let i = 0; i < this.ourLabels.length; i++) {
          this.charts.chartHistogramPerSection.data.labels.push(this.$t(this.tChart + this.ourLabels[i]))
          this.charts.chartHistogramOverall.data.labels.push(this.$t(this.tChart + this.ourLabels[i]))
          this.charts.chartMultipleHistograms.data.labels.push(this.$t(this.tChart + this.ourLabels[i]))
          this.charts.chartCategoryEndorsment.data.labels.push(this.$t(this.tChart + this.ourLabels[i]))
          this.charts.chartCategoryEndorsmentOverall.data.labels.push(this.$t(this.tChart + this.ourLabels[i]))
        }
        for (let i = 0; i < this.ourLabelsRadar.length; i++) {
          this.charts.radarChart.data.labels.push(this.$t(this.tChart + this.ourLabelsRadar[i]))
        }
        for (let i = 0; i < this.ourLabelsRadar.length; i++) {
          const entry = { index: i, name: this.ourLabelsRadar[i], label: this.$t(this.tChart + this.ourLabelsRadar[i]) }
          this.entries.push(entry)
        }
      }

      if (!userAdult) {
        for (let i = 0; i < this.ourKidLabels.length; i++) {
          this.charts.chartHistogramPerSection.data.labels.push(this.$t(this.tChart + this.ourKidLabels[i]))
          this.charts.chartHistogramOverall.data.labels.push(this.$t(this.tChart + this.ourKidLabels[i]))
          this.charts.chartMultipleHistograms.data.labels.push(this.$t(this.tChart + this.ourKidLabels[i]))
          this.charts.chartCategoryEndorsment.data.labels.push(this.$t(this.tChart + this.ourKidLabels[i]))
          this.charts.chartCategoryEndorsmentOverall.data.labels.push(this.$t(this.tChart + this.ourKidLabels[i]))
        }
        for (let i = 0; i < this.ourKidLabelsRadar.length; i++) {
          this.charts.radarChart.data.labels.push(this.$t(this.tChart + this.ourKidLabelsRadar[i]))
        }
        for (let i = 0; i < this.ourKidLabelsRadar.length; i++) {
          const entry = { index: i, name: this.ourKidLabelsRadar[i], label: this.$t(this.tChart + this.ourKidLabelsRadar[i]) }
          this.entries.push(entry)
        }
      }
      this.entries.unshift({ index: null, name: 'all', label: this.$t(this.tChart + 'all') })
      this.charts.chartHistogramPerSection.data.datasets[0].backgroundColor = [
        'rgba(75, 192, 192, 0.2)',
        'rgba(255, 205, 86, 0.2)',
        'rgba(255, 159, 64, 0.2)', 'rgba(255, 159, 64, 0.2)', 'rgba(255, 159, 64, 0.2)',
        'rgba(255, 159, 64, 0.2)', 'rgba(255, 159, 64, 0.2)', 'rgba(255, 159, 64, 0.2)',
        'rgba(255, 205, 86, 0.2)',
        'rgba(255, 159, 64, 0.2)', 'rgba(255, 159, 64, 0.2)', 'rgba(255, 159, 64, 0.2)',
        'rgba(255, 159, 64, 0.2)', 'rgba(255, 159, 64, 0.2)', 'rgba(255, 159, 64, 0.2)'
      ]
      this.charts.chartHistogramPerSection.data.datasets[0].borderColor = [
        'rgba(75, 192, 192, 1)',
        'rgba(255, 205, 86, 1)',
        'rgba(255, 159, 64, 1)', 'rgba(255, 159, 64, 1)', 'rgba(255, 159, 64, 1)',
        'rgba(255, 159, 64, 1)', 'rgba(255, 159, 64, 1)', 'rgba(255, 159, 64, 1)',
        'rgba(255, 205, 86, 1)',
        'rgba(255, 159, 64, 1)', 'rgba(255, 159, 64, 1)', 'rgba(255, 159, 64, 1)',
        'rgba(255, 159, 64, 1)', 'rgba(255, 159, 64, 1)', 'rgba(255, 159, 64, 1)'
      ]
    },
    setLabelsForTable () {
      let userAdult = this.getAge()
      let labels = []
      if (userAdult) {
        for (let i = 0; i < this.ourLabels.length; i++) {
          labels.push(this.$t(this.tChart + this.ourLabels[i]))
        }
      }
      if (!userAdult) {
        for (let i = 0; i < this.ourKidLabels.length; i++) {
          labels.push(this.$t(this.tChart + this.ourKidLabels[i]))
        }
      }
      return labels
    },
    getUserAge () {
      return new Date().getFullYear() - this.recipient.birth_year
    },
    computeGraphSatisfaction (sections, evaluation) {
      this.satisfactionGraphDatasets = []
      this.satisfactionGraphDatasets.push({ data: [], label: this.$t(this.getEvaluationSectionTitleSatisfaction(evaluation, 0)) })
      this.satisfactionGraphDatasets.push({ data: [], label: this.$t(this.getEvaluationSectionTitleSatisfaction(evaluation, 1)) })
      for (let index in sections) {
        if (typeof evaluation.data[0].children[index].children !== 'undefined') {
          let section = sections[index]
          let satisfaction = {
            really_satisfied: 0,
            satisfied: 0,
            unsatisfied: 0,
            really_unsatisfied: 0
          }
          for (let i = 0; i < section.step3.value.length; i++) {
            if (section.step3.value[i] !== null && satisfaction.hasOwnProperty(section.step3.value[i].result)) {
              satisfaction[section.step3.value[i].result]++
            }
          }

          let data = Object.values(satisfaction)
          this.satisfactionGraphDatasets.push({
            data: data,
            label: this.$t(this.getEvaluationSectionTitleSatisfaction(evaluation, index + 2))
          })
        }
      }
      // Total
      let total = [0, 0, 0, 0]
      for (let i = 2; i < this.satisfactionGraphDatasets.length; i++) {
        total[0] += this.satisfactionGraphDatasets[i].data[0]
        total[1] += this.satisfactionGraphDatasets[i].data[1]
        total[2] += this.satisfactionGraphDatasets[i].data[2]
        total[3] += this.satisfactionGraphDatasets[i].data[3]
      }
      this.satisfactionGraphDatasets[0].data = total

      // Daily Activity
      let dailyActivity = [0, 0, 0, 0]
      for (let i = 2; i < 8; i++) {
        dailyActivity[0] += this.satisfactionGraphDatasets[i].data[0]
        dailyActivity[1] += this.satisfactionGraphDatasets[i].data[1]
        dailyActivity[2] += this.satisfactionGraphDatasets[i].data[2]
        dailyActivity[3] += this.satisfactionGraphDatasets[i].data[3]
      }
      this.satisfactionGraphDatasets[1].data = dailyActivity

      // Social Roles
      this.satisfactionGraphDatasets.splice(8, 0, { data: [], label: this.$t(this.getEvaluationSectionTitleSatisfaction(evaluation, 8)) })
      let socialRoles = [0, 0, 0, 0]
      for (let i = 9; i < this.satisfactionGraphDatasets.length; i++) {
        socialRoles[0] += this.satisfactionGraphDatasets[i].data[0]
        socialRoles[1] += this.satisfactionGraphDatasets[i].data[1]
        socialRoles[2] += this.satisfactionGraphDatasets[i].data[2]
        socialRoles[3] += this.satisfactionGraphDatasets[i].data[3]
      }
      this.satisfactionGraphDatasets[8].data = socialRoles
    },
    __setLabelsForSatisfactionGraph () {
      let labels = []

      for (let i = 0; i < this.satisfactionLabels.length; i++) {
        labels.push(this.$t(this.satisfactionLabels[i]))
      }

      return labels
    },
    __prepareDataForSatisfactionGraph (index, dataset) {
      let clone = JSON.parse(JSON.stringify(this.satisfactionDataOptions))
      this.charts.satisfactionGraphs.data[index] = {
        labels: this.__setLabelsForSatisfactionGraph(),
        datasets: [{ ...clone.datasets[0], ...dataset }]
      }
      return this.charts.satisfactionGraphs.data[index]
    },
    __prepareOptionsForSatisfactionGraph (index) {
      this.charts.satisfactionGraphs.options[index] = {
        responsive: true,
        maintainAspectRatio: true,
        legend: {
          display: false
        },
        title: {
          display: true
        }
      }
      if (this.getUserAge() < 5) {
        this.charts.satisfactionGraphs.options[index].title.text = this.$t(this.getEvaluationSectionTitleSatisfaction(this.evaluationsTypes[0], index))
      }
      if (this.getUserAge() > 4 && this.getUserAge() < 14) {
        this.charts.satisfactionGraphs.options[index].title.text = this.$t(this.getEvaluationSectionTitleSatisfaction(this.evaluationsTypes[1], index))
      }
      if (this.getUserAge() > 13) {
        this.charts.satisfactionGraphs.options[index].title.text = this.$t(this.getEvaluationSectionTitleSatisfaction(this.evaluationsTypes[2], index))
      }
      return this.charts.satisfactionGraphs.options[index]
    },
    __computePaddingValues (data) {
      let data2 = []
      for (let i = 0; i < data.length; i++) {
        data2.push(100 - data[i])
      }
      return data2
    },
    computeGraphHistogramPerSection (sections, evaluation) {
      const entryType = this.entryType
      if (entryType.name === 'all') {
        this.charts.chartHistogramPerSection.data.datasets[0].data = this._computeSingleHistogramData(sections, evaluation)
        this.charts.chartHistogramPerSection.data.labels = []
        this.setLabels()
      } else {
        this.charts.chartHistogramPerSection.data.datasets[0].data = this._computeScoresForSection(entryType.index, sections, evaluation)
        this.charts.chartHistogramPerSection.data.labels = []
        for (let i = 0; i < this.charts.chartHistogramPerSection.data.datasets[0].data.length; i++) {
          this.charts.chartHistogramPerSection.data.datasets[0].borderColor = 'rgba(255, 159, 64, 1)'
          this.charts.chartHistogramPerSection.data.datasets[0].backgroundColor = 'rgba(255, 159, 64, 0.2)'
          this.charts.chartHistogramPerSection.data.labels.push(i.toString())
        }
      }
      this.$refs.graphHistogramPerSection.renderThat()
    },
    computeMultipleHistograms (entryType) {
      this.charts.chartMultipleHistograms.data.datasets = []
      const evaluation = this.getRightEvaluationByRecipientAge()[0]
      if (entryType.name === 'all') {
        this.setLabels()
        for (let i = 0; i < this.selectedSnapshots.length; i++) {
          this.charts.chartMultipleHistograms.data.datasets.push({})
          const color = this.generateColor(i)
          const sections = JSON.parse(this.selectedSnapshots[i].snapshot)
          this.charts.chartMultipleHistograms.data.datasets[i].label = this.selectedSnapshots[i].title
          this.charts.chartMultipleHistograms.data.datasets[i].borderColor = this.generateColorBorder(color)
          this.charts.chartMultipleHistograms.data.datasets[i].backgroundColor = this.generateColorBackground(color, 0.3)
          this.charts.chartMultipleHistograms.data.datasets[i].borderWidth = 1
          this.charts.chartMultipleHistograms.data.datasets[i].data = this._computeSingleHistogramData(sections, evaluation)
        }
      } else {
        this.charts.chartMultipleHistograms.data.labels = []
        for (let i = 0; i < this.selectedSnapshots.length; i++) {
          this.charts.chartMultipleHistograms.data.datasets.push({})
          const color = this.generateColor(i)
          const sections = JSON.parse(this.selectedSnapshots[i].snapshot)
          this.charts.chartMultipleHistograms.data.datasets[i].label = this.selectedSnapshots[i].title
          this.charts.chartMultipleHistograms.data.datasets[i].borderColor = this.generateColorBorder(color)
          this.charts.chartMultipleHistograms.data.datasets[i].backgroundColor = this.generateColorBackground(color, 0.3)
          this.charts.chartMultipleHistograms.data.datasets[i].borderWidth = 1
          this.charts.chartMultipleHistograms.data.datasets[i].data = this._computeScoresForSection(entryType.index, sections, evaluation)
        }
        for (let i = 0; i < this.charts.chartMultipleHistograms.data.datasets[0].data.length; i++) {
          this.charts.chartMultipleHistograms.data.labels.push(i.toString())
        }
      }
      this.$refs.graphMultipleHistograms.renderThat()
    },
    _computeScoresForSection (index, sections, evaluation) {
      if (typeof evaluation.data[0].children[index].children !== 'undefined') {
        const length = evaluation.data[0].children[index].children.length
        this.computeAllScores(sections[index], length)
        let scores = this.scores
        this.clearScores()
        return scores
      }
    },
    _computeSingleHistogramData (sections, evaluation) {
      let computedScores = []
      for (let index in sections) {
        if (typeof evaluation.data[0].children[index].children !== 'undefined') {
          const length = evaluation.data[0].children[index].children.length
          this.computeAllScores(sections[index], length)
          let scores = this.scores
          let avg = this.average(scores)

          this.clearScores()

          // array that contains average of scores by sections
          computedScores.push(avg)
        }
      }

      let chartData = []

      // Compute total
      chartData.push(this.average(computedScores))

      // Compute DailyActivity
      let clone1 = [...computedScores]
      let scoresDailyActivity = clone1.splice(0, 6)
      chartData.push(this.average(scoresDailyActivity))
      chartData = chartData.concat(scoresDailyActivity)

      // Compute SocialRole
      let clone2 = [...computedScores]
      let scoresSocialRole = clone2.splice(6)
      chartData.push(this.average(scoresSocialRole))
      chartData = chartData.concat(scoresSocialRole)

      return chartData
    },
    computeMultipleSatisfaction (entryType) {
      const evaluation = this.getRightEvaluationByRecipientAge()[0]
      let labels = []
      const sections = JSON.parse(this.selectedSnapshots[0].snapshot)
      if (entryType.name === 'all') {
        this.charts.chartMultipleSatisfaction.options.plugins.datalabels.display = true
        this.charts.chartMultipleSatisfaction.options.scales.yAxes[0].stacked = true
        for (let i = 0; i < sections.length + 3; i++) {
          labels.push(this.$t(this.getEvaluationSectionTitleSatisfaction(evaluation, i)))
        }
        this.charts.chartMultipleSatisfaction.data.labels = labels
        this._getSatisfactionSectionValues()
      } else {
        this.charts.chartMultipleSatisfaction.data.datasets = []
        this.charts.chartMultipleSatisfaction.options.plugins.datalabels.display = false
        this.charts.chartMultipleSatisfaction.options.scales.yAxes[0].stacked = false
        for (let i = 0; i < sections[entryType.index].step3.value.length; i++) {
          labels.push(i.toString())
        }
        for (let i = 0; i < this.selectedSnapshots.length; i++) {
          this._getSatisfactionEntriesValues(this.selectedSnapshots[i], entryType.index, i)
        }
        this.charts.chartMultipleSatisfaction.data.labels = labels
      }
      this.$refs.graphMultipleSatisfaction.renderThat()
    },
    _getSatisfactionEntriesValues (snapshot, idIndex, i) {
      const color = this.generateColor(i)
      const values = JSON.parse(snapshot.snapshot)[idIndex].step3.value
      this.charts.chartMultipleSatisfaction.data.datasets.push({})
      this.charts.chartMultipleSatisfaction.data.datasets[i].backgroundColor = this.generateColorBackground(color, 0.3)
      this.charts.chartMultipleSatisfaction.data.datasets[i].data = []
      for (let j = 0; j < values.length; j++) {
        if (values[j]) {
          this.charts.chartMultipleSatisfaction.data.datasets[i].label = ''
          switch (values[j].result) {
            case 'very_satisfied':
              this.charts.chartMultipleSatisfaction.data.datasets[i].data.push(100)
              break
            case 'satisfied':
              this.charts.chartMultipleSatisfaction.data.datasets[i].data.push(75)
              break
            case 'unsatisfied':
              this.charts.chartMultipleSatisfaction.data.datasets[i].data.push(50)
              break
            case 'very_unsatisfied':
              this.charts.chartMultipleSatisfaction.data.datasets[i].data.push(25)
              break
          }
        } else {
          this.charts.chartMultipleSatisfaction.data.datasets[i].data.push(0)
        }
      }
    },
    _getSatisfactionSectionValues () {
      const evaluation = this.getRightEvaluationByRecipientAge()[0]
      let bigMatrix = []
      for (let i = 0; i < this.selectedSnapshots.length; i++) {
        let singleData = []
        const sections = JSON.parse(this.selectedSnapshots[i].snapshot)
        for (let index in sections) {
          if (typeof evaluation.data[0].children[index].children !== 'undefined') {
            let section = sections[index]
            let satisfaction = {
              really_satisfied: 0,
              satisfied: 0,
              unsatisfied: 0,
              really_unsatisfied: 0
            }
            for (let i = 0; i < section.step3.value.length; i++) {
              if (section.step3.value[i] !== null && satisfaction.hasOwnProperty(section.step3.value[i].result)) {
                satisfaction[section.step3.value[i].result]++
              }
            }
            let data = Object.values(satisfaction)
            singleData.push(data)
          }
        }
        let total = [0, 0, 0, 0]
        for (let i = 0; i < singleData.length; i++) {
          total[0] += singleData[i][0]
          total[1] += singleData[i][1]
          total[2] += singleData[i][2]
          total[3] += singleData[i][3]
        }
        let dailyActivity = [0, 0, 0, 0]
        for (let i = 0; i < 6; i++) {
          dailyActivity[0] += singleData[i][0]
          dailyActivity[1] += singleData[i][1]
          dailyActivity[2] += singleData[i][2]
          dailyActivity[3] += singleData[i][3]
        }
        let socialRoles = [0, 0, 0, 0]
        for (let i = 6; i < singleData.length; i++) {
          socialRoles[0] += singleData[i][0]
          socialRoles[1] += singleData[i][1]
          socialRoles[2] += singleData[i][2]
          socialRoles[3] += singleData[i][3]
        }
        singleData.unshift(dailyActivity)
        singleData.unshift(total)
        singleData.splice(8, 0, socialRoles)
        bigMatrix.push(...singleData)
      }
      this.charts.chartMultipleSatisfaction.data.datasets = []
      const sectionLength = JSON.parse(this.selectedSnapshots[0].snapshot).length + 3
      const satisfactionRange = this.satisfactionColors.length
      const nbOfDatasets = satisfactionRange * this.selectedSnapshots.length
      let stackCpt = 0
      for (let i = 0; i < nbOfDatasets; i++) {
        this.charts.chartMultipleSatisfaction.data.datasets.push({})
        this.charts.chartMultipleSatisfaction.data.datasets[i].data = []
        this.charts.chartMultipleSatisfaction.data.datasets[i].label = this.$t(this.satisfactionLabels[i % satisfactionRange])
        this.charts.chartMultipleSatisfaction.data.datasets[i].backgroundColor = this.satisfactionColors[i % satisfactionRange]
        this.charts.chartMultipleSatisfaction.data.datasets[i].stack = (stackCpt)
        if (this.charts.chartMultipleSatisfaction.data.datasets.length % satisfactionRange === 0) {
          stackCpt++
        }
      }
      let index = 0
      for (let i = 0; i < this.selectedSnapshots.length; i++) {
        for (let l = 0; l < satisfactionRange; l++) {
          for (let j = i * sectionLength; j < i * sectionLength + sectionLength; j++) {
            let total = 0
            let percentageData = 0
            for (let k = 0; k < satisfactionRange; k++) {
              total += bigMatrix[j][k]
            }
            if (bigMatrix[j][l] !== 0) {
              percentageData = (bigMatrix[j][l] * 100 / total).toFixed(2)
            } else {
              percentageData = null
            }
            this.charts.chartMultipleSatisfaction.data.datasets[index].data.push(percentageData)
            if (this.charts.chartMultipleSatisfaction.data.datasets[index].data.length === sectionLength) {
              index++
            }
          }
        }
      }
    },
    computeGraphRadar () {
      this.charts.radarChart.data.datasets = [{}]
      for (let i = 0; i < this.selectedSnapshots.length; i++) {
        let randomColor = this.generateColor(i)
        this.charts.radarChart.data.datasets.push({})
        this.charts.radarChart.data.datasets[i].data = this.computeRadarSingleSnapshot(this.selectedSnapshots[i])
        this.charts.radarChart.data.datasets[i].label = this.selectedSnapshots[i].title
        this.charts.radarChart.data.datasets[i].backgroundColor = this.generateColorBackground(randomColor, 0.2)
        this.charts.radarChart.data.datasets[i].borderColor = this.generateColorBorder(randomColor)
      }
      let datasetLength = this.charts.radarChart.data.datasets.length - 1
      this.charts.radarChart.data.datasets[datasetLength].data = this.computeOverallRadar(this.charts.radarChart.data.datasets)
      this.charts.radarChart.data.datasets[datasetLength].label = this.$t(this.tChart + 'all')
      this.charts.radarChart.data.datasets[datasetLength].backgroundColor = 'rgba(0,0,0,0.2)'
      this.charts.radarChart.data.datasets[datasetLength].borderColor = 'rgba(0,0,0,1)'

      this.$refs.graphRadarChart.renderChart(this.charts.radarChart.data, this.charts.radarChart.options)
    },
    computeRadarSingleSnapshot (snapshot) {
      let sections = JSON.parse(snapshot.snapshot)
      const evaluation = this.getRightEvaluationByRecipientAge()[0]
      let computedScores = []
      for (let index in sections) {
        if (typeof evaluation.data[0].children[index].children !== 'undefined') {
          this.computeAllScores(sections[index], evaluation.data[0].children[index].children.length)
          let scores = this.scores
          let avg = this.average(scores)
          this.clearScores()

          computedScores.push(avg)
        }
      }
      return computedScores
    },
    computeOverallRadar (snapshotsComputed) {
      let overallRow = []
      if (typeof snapshotsComputed[0].data !== 'undefined') {
        for (let i = 0; i < snapshotsComputed[0].data.length; i++) {
          let value = 0
          let divider = 0
          for (let j = 0; j < snapshotsComputed.length - 1; j++) {
            try {
              value = value + snapshotsComputed[j].data[i]
              if (snapshotsComputed[j].data[i] > 0) {
                divider = divider + 1
              }
            } catch (error) {
            }
          }
          let avg = 0
          if (divider > 0) {
            avg = value / divider
          }
          overallRow.push(avg)
        }
      }
      return overallRow
    },
    /**
     * This method will compute the overall score for the current age of the recipient
     * If the recipient is a kid, then it will take all kid snapshots and compute the avg.
     */
    computeGraphHistogramOverall () {
      // get the right evaluation by recipient age
      let evaluation = this.getRightEvaluationByRecipientAge()[0]

      // get snapshots according to the evaluation type (kid, young, adult)
      let snapshots = this.getSnapshotsByEvaluationsType(evaluation)

      // compute all snapshots
      let snapshotsComputedScores = []
      for (let i in snapshots) {
        let snapshot = snapshots[i]
        if (typeof snapshot.snapshot !== 'undefined') {
          let sections = JSON.parse(snapshot.snapshot)
          let computedScores = []

          for (let index in sections) {
            if (typeof evaluation.data[0].children[index].children !== 'undefined') {
              this.computeAllScores(sections[index], evaluation.data[0].children[index].children.length)
              let scores = this.scores
              let avg = this.average(scores)

              this.clearScores()

              // array that contains average of scores by sections
              computedScores.push(avg)
            }
          }
          snapshotsComputedScores.push(computedScores)
        }
      }

      // sum all snapshots
      let computedScores = []
      for (let i = 0; i < snapshotsComputedScores.length; i++) {
        for (let j = 0; j < snapshotsComputedScores[i].length; j++) {
          if (typeof computedScores[j] === 'undefined') {
            computedScores[j] = 0
          }
          computedScores[j] += snapshotsComputedScores[i][j]
        }
      }

      // divide them by the nbr of evaluation to get the avg
      for (let i = 0; i < computedScores.length; i++) {
        computedScores[i] = computedScores[i] / snapshotsComputedScores.length
      }

      // compute data to put them in the chart
      let chartData = []

      // Compute total
      chartData.push(this.average(computedScores))

      // Compute DailyActivity
      let clone1 = [...computedScores]
      let scoresDailyActivity = clone1.splice(0, 6)
      chartData.push(this.average(scoresDailyActivity))
      chartData = chartData.concat(scoresDailyActivity)

      // Compute SocialRole
      let clone2 = [...computedScores]
      let scoresSocialRole = clone2.splice(6)
      chartData.push(this.average(scoresSocialRole))
      chartData = chartData.concat(scoresSocialRole)

      // render the chart
      this.charts.chartHistogramOverall.data.datasets[0].data = chartData
      this.$refs.graphHistogramOverall.renderThat()
    },
    computeGraphSatisfactionOverall (evaluation) {
      // get snapshots according to the evaluation type (kid, young, adult)
      let snapshots = this.selectedSnapshots

      let snapshotsSatisfactionsTotal = []
      for (let i in snapshots) {
        let snapshot = snapshots[i]
        if (typeof snapshot.snapshot !== 'undefined') {
          let sections = JSON.parse(snapshot.snapshot)

          let snapshotSatisfactionData = []
          for (let index in sections) {
            if (typeof evaluation.data[0].children[index].children !== 'undefined') {
              let section = sections[index]

              let satisfaction = {
                really_satisfied: 0,
                satisfied: 0,
                unsatisfied: 0,
                really_unsatisfied: 0
              }
              for (let i = 0; i < section.step3.value.length; i++) {
                if (section.step3.value[i] !== null && satisfaction.hasOwnProperty(section.step3.value[i].result)) {
                  satisfaction[section.step3.value[i].result]++
                }
              }

              let data = Object.values(satisfaction)

              snapshotSatisfactionData.push({
                data: data,
                label: this.$t(this.getEvaluationSectionTitle(evaluation, index))
              })
            }
          }
          snapshotsSatisfactionsTotal.push(snapshotSatisfactionData)
        }
      }

      this.satisfactionGraphOverallDatasets = []

      if (snapshotsSatisfactionsTotal.length >= 1) {
        let clone = JSON.parse(JSON.stringify(snapshotsSatisfactionsTotal[0]))
        for (let i = 1; i < snapshotsSatisfactionsTotal.length; i++) {
          let snapshotSatisfactions = snapshotsSatisfactionsTotal[i]
          for (let j = 0; j < snapshotSatisfactions.length; j++) {
            for (let k = 0; k < snapshotSatisfactions[j].data.length; k++) {
              clone[j].data[k] += snapshotSatisfactions[j].data[k]
            }
          }
        }

        for (let i = 0; i < clone.length; i++) {
          for (let j = 0; j < clone[i].data.length; j++) {
            clone[i].data[j] = clone[i].data[j] / snapshotsSatisfactionsTotal.length
          }
        }

        this.satisfactionGraphOverallDatasets = clone
      }
    },
    __prepareDataForSatisfactionGraphOverall (index, dataset) {
      let clone = JSON.parse(JSON.stringify(this.satisfactionDataOverallOptions))
      this.charts.satisfactionGraphsOverall.data[index] = {
        labels: this.__setLabelsForSatisfactionGraph(),
        datasets: [{ ...clone.datasets[0], ...dataset }]
      }
      return this.charts.satisfactionGraphsOverall.data[index]
    },
    __prepareOptionsForSatisfactionGraphOverall (index) {
      this.charts.satisfactionGraphsOverall.options[index] = {
        responsive: true,
        maintainAspectRatio: true,
        legend: {
          display: false
        },
        title: {
          display: true
        }
      }
      if (this.getUserAge() < 5) {
        this.charts.satisfactionGraphsOverall.options[index].title.text = this.$t(this.getEvaluationSectionTitle(this.evaluationsTypes[0], index))
      }
      if (this.getUserAge() > 4 && this.getUserAge() < 14) {
        this.charts.satisfactionGraphsOverall.options[index].title.text = this.$t(this.getEvaluationSectionTitle(this.evaluationsTypes[1], index))
      }
      if (this.getUserAge() > 13) {
        this.charts.satisfactionGraphsOverall.options[index].title.text = this.$t(this.getEvaluationSectionTitle(this.evaluationsTypes[2], index))
      }
      return this.charts.satisfactionGraphsOverall.options[index]
    },
    onVisualize (snapshots, reportType, graphType, entryType) {
      this.reportType = reportType
      this.graphType = graphType
      this.selectedSnapshots = snapshots
      this.entryType = entryType
      const oneSnapshot = this.selectedSnapshots.length === 1
      const multipleSnapshots = this.selectedSnapshots.length > 1
      const evaluation = this.getRightEvaluationByRecipientAge()[0]
      let section = null

      if (oneSnapshot || multipleSnapshots) {
        this.currentSnapshot = this.selectedSnapshots[0]
        section = JSON.parse(this.currentSnapshot.snapshot)
      }
      if (oneSnapshot && this.reportType === 'scores' && this.graphType === 'avg_histogram') {
        this.computeGraphHistogramPerSection(section, evaluation)
        this.allowDisplay()
      } else if (oneSnapshot && this.reportType === 'satisfaction' && this.graphType === 'pies') {
        this.computeGraphSatisfaction(section, evaluation)
        this.allowDisplay()
      } else if (oneSnapshot && this.reportType === 'summary' && this.graphType === null) {
        this.computeGraphHistogramPerSection(section, evaluation)
        this.computeGraphSatisfaction(section, evaluation)
        this.computeGraphRadar()
        this.allowDisplay()
      } else if (multipleSnapshots && this.reportType === 'scores' && this.graphType === 'avg_histogram') {
        this.computeMultipleHistograms(this.entryType)
        this.allowDisplay()
      } else if (multipleSnapshots && this.reportType === 'satisfaction' && this.graphType === 'stacked_bars') {
        this.computeMultipleSatisfaction(this.entryType)
        this.allowDisplay()
      } else if (this.reportType === 'scores' && this.graphType === 'radar') {
        this.computeGraphRadar()
        this.allowDisplay()
      } else if (multipleSnapshots && this.reportType === 'summary' && this.graphType === null) {
        this.computeMultipleHistograms({ name: 'all' })
        this.computeMultipleSatisfaction({ name: 'all' })
        this.computeGraphRadar()
        this.allowDisplay()
      } else if (this.graphType === 'table') {
        try {
          this.$nextTick(() => {
            setTimeout(() => {
              this.$refs.reportTable.reload()
              this.$refs.reportTable.$forceUpdate()
            }, 50)
          })
        } catch (e) {
          console.log(e)
        }
        this.allowDisplay()
      } else {
        this.disallowDisplay()
      }
    },
    /* UTILS */
    getRightEvaluationByRecipientAge () {
      this.userAge = this.getUserAge()
      // kid
      if (this.userAge >= 0 && this.userAge <= 4) {
        return this.getEvaluation(1)
        // young
      } else if (this.userAge > 4 && this.userAge <= 13) {
        return this.getEvaluation(2)
        // adult
      } else {
        return this.getEvaluation(3)
      }
    },
    getSnapshotsByEvaluationsType (evaluation) {
      let snapshots = []
      if (evaluation.title === 'kid') {
        snapshots = this.snapshotKid
      } else if (evaluation.title === 'young') {
        snapshots = this.snapshotYoung
      } else if (evaluation.title === 'adult') {
        snapshots = this.snapshotAdult
      }
      return snapshots
    },
    generateColor (i) {
      const length = this.colors.length
      return this.colors[i % length]
    },
    generateColorBackground (color, opacity) {
      return color + opacity + ')'
    },
    generateColorBorder (color) {
      return color + '1)'
    },
    checkCombo () {
      const oneSnapshot = this.selectedSnapshots.length === 1
      const multipleSnapshots = this.selectedSnapshots.length > 1

      if (oneSnapshot && this.reportType === 'scores' && this.graphType === 'avg_histogram') {
        return true
      } else if (oneSnapshot && this.reportType === 'satisfaction' && this.graphType === 'pies') {
        return true
      } else if (oneSnapshot && this.reportType === 'summary' && !this.graphType) {
        return true
      } else if (multipleSnapshots && this.reportType === 'scores' && this.graphType === 'avg_histogram') {
        return true
      } else if (this.reportType === 'scores' && this.graphType === 'table') {
        return true
      } else if (multipleSnapshots && this.reportType === 'satisfaction' && this.graphType === 'stacked_bars') {
        return true
      } else if (this.reportType === 'satisfaction' && this.graphType === 'table') {
        return true
      } else if (this.reportType === 'scores' && this.graphType === 'radar') {
        return true
      } else return multipleSnapshots && this.reportType === 'summary' && !this.graphType
    },
    /*
    *** Manage combos
    */
    allowDisplay () {
      this.displayAll = true
    },
    disallowDisplay () {
      this.displayAll = false
    },
    /*
   *** PDF GENERATOR
    */
    logoPDF () {
      // Load RIPPH Logo
      this.docPDF.addImage(this.logo, 'png', 5, 10)
    },
    titlePDF () {
      this.docPDF.setFontSize(28)
      this.docPDF.text(this.$t(this.labelsPDF.title), 10, 55)
      this.docPDF.setFontSize(14)
      const subTitle = this.graphType ? (this.$t('reports.types.' + this.reportType) + ' - ' + this.$t('reports.types.' + this.graphType)) : this.$t('reports.types.' + this.reportType)
      this.docPDF.text(subTitle, 10, 65)
    },
    contentPDF () {
      // Pattern is different if overview is selected or not
      let overview = this.selectedSnapshots.length > 1
      this.writePDF(overview)
    },
    writePDF (overview) {
      if (overview) {
        this.writeTextOverviewPDF()
      } else {
        this.writeTextSpecificPDF()
      }
      this.printChartsPDF(overview)
    },
    writeTextSpecificPDF () {
      // Titles
      this.docPDF.setFontSize(12)
      this.docPDF.setFontType('bold')
      this.docPDF.text(this.$t(this.labelsPDF.associationTitle), 10, 80)
      this.docPDF.text(this.$t(this.labelsPDF.prof), 10, 87)
      this.docPDF.text(this.$t(this.labelsPDF.recipient), 10, 94)
      this.docPDF.text(this.$t(this.labelsPDF.snapshotName), 10, 101)
      this.docPDF.text(this.$t(this.labelsPDF.creationDate), 10, 108)
      this.docPDF.text(this.$t(this.labelsPDF.lastUpdate), 10, 115)
      this.docPDF.text(this.$t('comment') + " : ", 10, 122)
      // Data
      this.docPDF.setFontType('normal')
      this.docPDF.text(this.user.organizationName, 105, 80)
      this.docPDF.text(this.user.first_name + ' ' + this.user.last_name, 105, 87)
      this.docPDF.text(this.recipient.nick_name, 105, 94)
      this.docPDF.text(this.currentSnapshot.title.substring(0, 40), 105, 101)
      this.docPDF.text(this.currentSnapshot.created_at, 105, 108)
      this.docPDF.text(this.currentSnapshot.updated_at, 105, 115)
      for (let i = 0; i < this.comment.match(/.{1,40}/g).length; i++) {
        this.docPDF.text(this.comment.match(/.{1,40}/g), 105, 122)
      }
    },
    writeTextOverviewPDF () {
      // Title
      this.docPDF.setFontSize(12)
      this.docPDF.setFontType('bold')
      this.docPDF.text(this.$t(this.labelsPDF.associationTitle), 10, 80)
      this.docPDF.text(this.$t(this.labelsPDF.prof), 10, 87)
      this.docPDF.text(this.$t(this.labelsPDF.recipient), 10, 94)
      // Data
      this.docPDF.setFontType('normal')
      this.docPDF.text(this.user.organizationName, 105, 80)
      this.docPDF.text(this.user.first_name + ' ' + this.user.last_name, 105, 87)
      this.docPDF.text(this.recipient.nick_name, 105, 94)
      for (let i = 0; i < this.comment.match(/.{1,40}/g).length; i++) {
        this.docPDF.text(this.comment.match(/.{1,40}/g), 105, 101)
      }
    },
    printChartsPDF (overview) {
      const oneSnapshot = this.selectedSnapshots.length === 1
      const multipleSnapshots = this.selectedSnapshots.length > 1
      let height = 120
      if (overview) { height = 99 }

      if (oneSnapshot && this.reportType === 'scores' && this.graphType === 'avg_histogram') {
        this.docPDF.addPage('a4', 'landscape')
        height = 20
        height += this.printSingleScoresPDF(height)
      } else if (oneSnapshot && this.reportType === 'satisfaction' && this.graphType === 'pies') {
        this.docPDF.addPage('a4', 'landscape')
        height = 20
        height += this.printSingleSatisfactionPDF()
      } else if (oneSnapshot && this.reportType === 'summary' && this.graphType === null) {
        this.docPDF.addPage('a4', 'landscape')
        height = 20
        height += this.printSingleScoresPDF(height)
        this.docPDF.addPage('a4', 'landscape')
        height += this.printSingleSatisfactionPDF()
      } else if (multipleSnapshots && this.reportType === 'scores' && this.graphType === 'avg_histogram') {
        this.docPDF.addPage('a4', 'landscape')
        height = 20
        height += this.printMultipleScoresPDF(height)
      } else if (multipleSnapshots && this.reportType === 'satisfaction' && this.graphType === 'stacked_bars') {
        this.docPDF.addPage('a4', 'landscape')
        height = 20
        height += this.printMultipleSatisfactionPDF(height)
      } else if (this.graphType === 'radar') {
        height += this.printChartRadarPDF(height)
      } else if (multipleSnapshots && this.reportType === 'summary' && this.graphType === null) {
        height += this.printChartRadarPDF(height)
        this.docPDF.addPage('a4', 'landscape')
        height = 20
        height += this.printMultipleScoresPDF(height)
        height += this.printMultipleSatisfactionPDF(height)
      }
    },
    printSingleScoresPDF (height) {
      let tempHeight = height
      // Title
      this.docPDF.setFontSize(22)
      this.docPDF.text(this.$t(this.labelsPDF.scoresChart), 5, tempHeight)
      // Load scores graph as an image
      tempHeight += 5
      let chartData = this.formatBarCharts('#scores-chart')
      this.docPDF.addImage(chartData.image, 'JPEG', 5, tempHeight, 287, 287 * chartData.ratio)
      tempHeight += 85
      return tempHeight - height
    },
    printSingleSatisfactionPDF () {
      this.docPDF.setFontSize(22)
      this.docPDF.text(this.$t(this.labelsPDF.satisfactionChart), 10, 25)
      this.addSatisfactionCaptionPDF()
      // Number of pies depends on the age of the recipient
      let n
      if (this.currentSnapshot !== null && typeof this.currentSnapshot !== 'undefined') {
        n = this.getEvaluation(this.currentSnapshot.evaluation_id)[0].data[0].children.length
      } else {
        n = this.getRightEvaluationByRecipientAge()[0].data[0].children.length
      }
      // Initial position
      let x = 8
      let y = 50
      // Change graphs position for each pie
      for (let i = 0; i < n; i++) {
        let canvasId = '#satisfaction-chart-' + i
        if (i === 4) {
          x = 8
          y += 50
        }
        if (i === 8) {
          x = 8
          y += 50
        }
        this.printSatisfactionGraphs(x, y, canvasId)
        x += 70
      }
    },
    printMultipleScoresPDF (height) {
      let tempHeight = height
      // Title
      this.docPDF.setFontSize(22)
      this.docPDF.text(this.$t(this.labelsPDF.scoresChart), 5, tempHeight)
      // Load scores graph as an image
      tempHeight += 5
      let chartData = this.formatBarCharts('#multiple-scores-chart')
      this.docPDF.addImage(chartData.image, 'JPEG', 5, tempHeight, 287, 287 * chartData.ratio)
      tempHeight += 95
      return tempHeight - height
    },
    printMultipleSatisfactionPDF (height) {
      let tempHeight = height
      // Title
      this.docPDF.setFontSize(22)
      this.docPDF.text(this.$t(this.labelsPDF.satisfactionChart), 5, tempHeight)
      // Load scores graph as an image
      tempHeight += 5
      let chartData = this.formatBarCharts('#multiple-satisfaction-chart')
      this.docPDF.addImage(chartData.image, 'JPEG', 5, tempHeight, 287, 287 * chartData.ratio)
      tempHeight += 95
      return tempHeight - height
    },
    printChartRadarPDF (height) {
      let tempHeight = height
      // Get the radar graph as an image
      let canvasChart = $('#radar-chart')[0]
      let heightChart = canvasChart.height
      let widthChart = canvasChart.width
      let ratioChart = heightChart / widthChart
      let base64Chart = canvasChart.toDataURL('image/png')
      tempHeight += 10
      this.docPDF.addImage(base64Chart, 'JPEG', 40, tempHeight, 125, 125 * ratioChart)
      tempHeight += 125
      return tempHeight - height
    },
    printSatisfactionGraphs (x, y, canvasId) {
      // Get image for pies
      let canvasChart = $(canvasId)[0]
      let heightChart = canvasChart.height
      let widthChart = canvasChart.width
      let ratioChart = heightChart / widthChart
      let base64Chart = canvasChart.toDataURL('image/png')
      this.docPDF.addImage(base64Chart, 'JPEG', x, y, 75, 75 * ratioChart)
    },
    addSatisfactionCaptionPDF () {
      this.docPDF.setFontSize(12)
      // First caption
      this.docPDF.text(this.$t('evaluations.head.criteria.very_satisfied'), 10, 38)
      this.docPDF.setDrawColor(0)
      this.docPDF.setFillColor(216, 228, 188)
      this.docPDF.rect(40, 34, 12, 6, 'FD')
      // Second caption
      this.docPDF.text(this.$t('evaluations.head.criteria.satisfied'), 80, 38)
      this.docPDF.setDrawColor(0)
      this.docPDF.setFillColor(255, 255, 204)
      this.docPDF.rect(110, 34, 12, 6, 'FD')
      // Third caption
      this.docPDF.text(this.$t('evaluations.head.criteria.unsatisfied'), 150, 38)
      this.docPDF.setDrawColor(0)
      this.docPDF.setFillColor(255, 181, 108)
      this.docPDF.rect(180, 34, 12, 6, 'FD')
      // Fourth caption
      this.docPDF.text(this.$t('evaluations.head.criteria.very_unsatisfied'), 220, 38)
      this.docPDF.setDrawColor(0)
      this.docPDF.setFillColor(237, 82, 70)
      this.docPDF.rect(250, 34, 12, 6, 'FD')
    },
    generatePDF () {
      // Load the whole PDF document
      this.docPDF = new jsPDF()
      this.logoPDF()
      this.titlePDF()
      this.contentPDF()
    },
    downloadPDF () {
      // Download the PDF
      this.setupDocTitle()
      this.$refs.warningPDF.show()
    },
    closeWarningPDF () {
      this.$refs.warningPDF.hide()
    },
    okWarningPDF () {
      this.generatePDF()
      this.docPDF.save(this.docTitle + '.pdf')
      this.$refs.warningPDF.hide()
    },
    /*
    *** GENERATE WORD
     */
    exportToWord () {
      const oneSnapshot = this.selectedSnapshots.length === 1
      const multipleSnapshots = this.selectedSnapshots.length > 1
      // Text data
      let divElem = document.getElementById('exportWord').cloneNode(true)
      let comDoc = document.createElement('comment')
      comDoc.innerHTML = 'commentaire'+ ' : ' +this.comment
      // RIPPH Logo
      let imgRIPPH = document.createElement('img')
      let titleScore = document.createElement('h3')
      titleScore.innerHTML = this.$t(this.labelsPDF.scoresChart)
      let titleSatisfaction = document.createElement('h3')
      titleSatisfaction.innerHTML = this.$t(this.labelsPDF.satisfactionChart)
      let titleRadar = document.createElement('h3')
      titleRadar.innerHTML = this.$t(this.labelsPDF.radarChart)
      imgRIPPH.src = this.logo
      // Creating Word content in HTML
      const allWord = document.createElement(('a'))
      allWord.appendChild(imgRIPPH)
      allWord.appendChild(document.createElement('br'))
      allWord.appendChild(divElem)
      allWord.appendChild(document.createElement('br'))
      allWord.appendChild(comDoc)
      allWord.appendChild(document.createElement('br'))
      let imgData

      // Generate graphs according to what's selected
      if (oneSnapshot && this.reportType === 'scores' && this.graphType === 'avg_histogram') {
        let imgScoresElem = document.createElement('img')
        imgData = this.formatBarCharts('#scores-chart')
        imgScoresElem.src = imgData.image
        allWord.appendChild(titleScore)
        allWord.appendChild(imgScoresElem)
      } else if (oneSnapshot && this.reportType === 'satisfaction' && this.graphType === 'pies') {
        const divCaption = this.captionsGenerator()
        let imgSatisfactionElem = this.createSatisfactionElems('#satisfaction-chart-')
        allWord.appendChild(divCaption)
        allWord.appendChild(titleSatisfaction)
        allWord.appendChild(imgSatisfactionElem)
      } else if (oneSnapshot && this.reportType === 'summary' && this.graphType === null) {
        let imgScoresElem = document.createElement('img')
        imgData = this.formatBarCharts('#scores-chart')
        imgScoresElem.src = imgData.image
        let imgSatisfactionElem = this.createSatisfactionElems('#satisfaction-chart-')
        let imgRadarElem = document.createElement('img')
        imgData = this.formatBarCharts('#radar-chart')
        imgRadarElem.src = imgData.image
        allWord.appendChild(titleRadar)
        allWord.appendChild(imgRadarElem)
        allWord.appendChild(titleScore)
        allWord.appendChild(imgScoresElem)
        allWord.appendChild(titleSatisfaction)
        allWord.appendChild(imgSatisfactionElem)
      } else if (multipleSnapshots && this.reportType === 'scores' && this.graphType === 'avg_histogram') {
        let imgScoresElem = document.createElement('img')
        imgData = this.formatBarCharts('#multiple-scores-chart')
        imgScoresElem.src = imgData.image
        allWord.appendChild(titleScore)
        allWord.appendChild(imgScoresElem)
      } else if (multipleSnapshots && this.reportType === 'satisfaction' && this.graphType === 'stacked_bars') {
        let imgSatisfactionElem = document.createElement('img')
        const divCaption = this.captionsGenerator()
        imgData = this.formatBarCharts('#multiple-satisfaction-chart')
        imgSatisfactionElem.src = imgData.image
        allWord.appendChild(titleSatisfaction)
        allWord.appendChild(divCaption)
        allWord.appendChild(imgSatisfactionElem)
      } else if (multipleSnapshots && this.reportType === 'comparison' && this.graphType === 'radar') {
        let imgRadarElem = document.createElement('img')
        imgData = this.formatBarCharts('#radar-chart')
        imgRadarElem.src = imgData.image
        allWord.appendChild(titleRadar)
        allWord.appendChild(imgRadarElem)
      } else if (multipleSnapshots && this.reportType === 'summary' && this.graphType === null) {
        let imgRadarElem = document.createElement('img')
        imgData = this.formatBarCharts('#radar-chart')
        imgRadarElem.src = imgData.image
        let imgScoresElem = document.createElement('img')
        imgData = this.formatBarCharts('#multiple-scores-chart')
        imgScoresElem.src = imgData.image
        let imgSatisfactionElem = document.createElement('img')
        imgData = this.formatBarCharts('#multiple-satisfaction-chart')
        imgSatisfactionElem.src = imgData.image
        allWord.appendChild(titleRadar)
        allWord.appendChild(imgRadarElem)
        allWord.appendChild(document.createElement('br'))
        allWord.appendChild(titleScore)
        allWord.appendChild(imgScoresElem)
        allWord.appendChild(document.createElement('br'))
        allWord.appendChild(titleSatisfaction)
        allWord.appendChild(imgSatisfactionElem)
        allWord.appendChild(document.createElement('br'))
      }

      // Generate Word Doc
      let sourceDoc = wordTemplate.replace('[TITLE]', this.docTitle).replace('HTML', this.preHtml + allWord.innerHTML + this.postHtml)
      let filename = this.docTitle + '.html'
      let blob = new Blob([sourceDoc], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })

      if (window.top.navigator.msSaveOrOpenBlob) {
        // For IE
        window.top.navigator.msSaveOrOpenBlob(blob, filename)
      } else {
        // For others
        document.body.appendChild(allWord)
        allWord.href = URL.createObjectURL(blob)
        allWord.download = filename
        allWord.style.display = 'none'
        allWord.click()
        allWord.parentNode.removeChild(allWord)
      }
    },
    captionGenerator (id) {
      // Text
      let divString = 'caption' + id
      let div = document.getElementById(divString).cloneNode(true)
      // Img
      let imgElem = document.createElement('img')
      // Load the img
      let logo = new Image()
      let c = document.createElement('canvas')
      let img = document.getElementById(id + 'id')
      c.height = img.naturalHeight
      c.width = img.naturalWidth
      let ctx = c.getContext('2d')
      ctx.drawImage(img, 0, 0, c.width, c.height)
      logo = c.toDataURL()
      imgElem.src = logo

      let captionDiv = document.createElement('div')
      captionDiv.appendChild(div)
      captionDiv.appendChild(imgElem)

      return captionDiv
    },
    captionsGenerator () {
      let divVS = this.captionGenerator('VS')
      let divS = this.captionGenerator('S')
      let divU = this.captionGenerator('U')
      let divVU = this.captionGenerator('VU')

      let allCaptions = document.createElement('div')
      allCaptions.appendChild(divVS)
      allCaptions.appendChild(divS)
      allCaptions.appendChild(divU)
      allCaptions.appendChild(divVU)

      return allCaptions
    },
    createSatisfactionElems (canvasId) {
      let div = document.createElement('div')
      div.setAttribute('class', 'col-3')
      let imgSatisfaction
      let imgData
      // Number of pies depends on the age of the recipient
      let n
      if (this.currentSnapshot !== null && typeof this.currentSnapshot !== 'undefined') {
        n = this.getEvaluation(this.currentSnapshot.evaluation_id)[0].data[0].children.length
      } else {
        n = this.getRightEvaluationByRecipientAge()[0].data[0].children.length
      }
      // Add img elements to the div
      for (let i = 0; i < n; i++) {
        imgSatisfaction = document.createElement('img')
        imgData = this.formatBarCharts(canvasId + i)
        imgSatisfaction.src = imgData.image
        div.appendChild(imgSatisfaction)
      }

      return div
    },
    downloadWord () {
      this.setupDocTitle()
      this.$refs.warningWord.show()
    },
    closeWarningWord () {
      this.$refs.warningWord.hide()
    },
    okWarningWord () {
      this.exportToWord()
      this.$refs.warningWord.hide()
    },
    /*
    *** UTILS FOR DOCUMENTS
     */
    formatBarCharts (canvasId) {
      // Get image for bars
      let canvasChart = $(canvasId)[0]
      let base64Chart = canvasChart.toDataURL('image/png')
      let heightChart = canvasChart.height
      let widthChart = canvasChart.width
      let ratioChart = heightChart / widthChart

      let formatedImage = {
        image: base64Chart,
        ratio: ratioChart
      }
      return formatedImage
    },
    setupDocTitle () {
      // Doc Title = Report_Name_Date
      this.docTitle = 'evaluation.pdf.pdftitle'
      let date = new Date()
      let dateString = this.formateDate(date)
      this.docTitle = this.$t(this.docTitle) + '_' + this.recipient.nick_name +
              '_' + dateString
    },
    formateDate (date) {
      let day = String(date.getDate())
      let month = String(date.getMonth() + 1)
      let year = String(date.getFullYear())

      return day + month + year
    },
    /*
    *** CSV EXPORT
     */
    downloadCsv (snapshots) {
      let data = []
      let filename

      for (let i = 0; i < snapshots.length; i++) {
        data.push(this.snapshot_to_array(snapshots[i]))
      }

      if (data.length > 1) {
        for (let i = 1; i < snapshots.length; i++) {
          Array.prototype.push.apply(data[0], data[i])
        }
      }

      const title = this.recipient.nick_name

      if (snapshots.length === 1) {
        filename = 'export_' + title + '_' + snapshots[0].title + '.csv'
      } else {
        filename = 'export_' + title + '.csv'
      }

      this.createCsv(data[0], filename)
    },
    snapshot_to_array (snapshot) {
      const rawObj = JSON.parse(snapshot.snapshot)
      let snap = []
      for (let i = 0; i < rawObj.length; i++) {
        for (let j = 0; j < rawObj[i].step1.value.length; j++) {
          if (rawObj[i].step1.value[j] !== null) {
            let entry = {}
            entry.file_number = this.recipient.recipient.file_number !== null ? this.recipient.recipient.file_number : this.recipient.id
            entry.snapshot_id = snapshot.id
            entry.entry_id = rawObj[i].step1.value[j].id
            entry.step1 = rawObj[i].step1.value[j].result
            if (entry.step1 === 'done') {
              entry.step21 = rawObj[i].step2.helpType.technicalHelp[j]
              entry.step22 = rawObj[i].step2.helpType.amenagement[j]
              entry.step23 = rawObj[i].step2.helpType.value[j].result
              entry.step24 = rawObj[i].step2.difficultyLevel.value[j].result
            } else {
              entry.step21 = 'na'
              entry.step22 = 'na'
              entry.step23 = 'na'
              entry.step24 = 'na'
            }
            if (entry.step1 === 'done' || entry.step1 === 'not') {
              entry.step3 = rawObj[i].step3.value[j].result
            } else {
              entry.step3 = 'na'
            }
            entry.score = this.getScore(entry)
            snap.push(entry)
          }
        }
      }
      return snap
    },
    getScore (entry) {
      switch (entry.step1) {
        case 'done':
          let helpTypeIndex = 0
          let difficultyIndex = 0
          switch (entry.step23) {
            case 'no_help':
              helpTypeIndex = 0
              break
            case 'light':
              helpTypeIndex = 1
              break
            case 'heavy':
              helpTypeIndex = 2
              break
            case 'full':
              helpTypeIndex = 3
              break
            default:
              helpTypeIndex = 0
          }
          switch (entry.step24) {
            case 'low':
              difficultyIndex = 0
              break
            case 'medium':
              difficultyIndex = 1
              break
            case 'heavy':
              difficultyIndex = 2
              break
            default:
              difficultyIndex = 0
          }
          let finalScore = this.calculationMatrix[difficultyIndex][helpTypeIndex]
          if (entry.step21) {
            finalScore += 3
          }
          if (entry.step22) {
            finalScore += 5
          }
          return (20 - finalScore) / 2
        case 'na':
          return 'na'
        case 'not':
          return 0
      }
    },
    createCsv (arrData, filename) {
      if (typeof filename === 'undefined') {
        filename = 'export.csv'
      }

      let csvContent = 'data:text/csv;charset=utf-8,'
      csvContent += 'recipient,snapshot_id,entry_id,doable,technical_help,arrangement,human_help,difficulty,satisfaction,score\r\n'

      for (let i = 0; i < arrData.length; i++) {
        let line = ''
        for (let index in arrData[i]) {
          if (line !== '') {
            line += ','
          }
          line += arrData[i][index] !== null ? arrData[i][index] : 'empty'
        }
        csvContent += line + '\r\n'
      }

      const data = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', data)
      link.setAttribute('download', filename)
      link.click()
    }
  }
}
</script>

<style lang="scss">
label,
textarea {
  font-size: .8rem;
  letter-spacing: 1px;
}
textarea {
  padding: 10px;
  max-width: 100%;
  line-height: 1.5;
  border-radius: 5px;
  border: 1px solid #ccc;
  box-shadow: 1px 1px 1px #999;
}

label {
  display: block;
  margin-bottom: 10px;
  font-weight: bold;
}

</style>



// WEBPACK FOOTER //
// ../../../../resources/js/components/Reports.vue